import React from 'react';
import { haversineKm, getMassSubLabel } from '../../utils/priceCalculator';
import { massLabel } from '../../utils/massCatalog';
import { toDate, timeLabel, kmLabel } from '../../utils/orderCardFormat';
import { hasFixedPrice, canTake, canBid } from '../../utils/orderPricing';
import { isBidOrder } from '../../utils/orderExpiry';
import { loadingEquipmentText } from '../../utils/loadingEquipment';
import { orderTruckText, orderLoadsText, orderLoadSplitText, orderCraneLiftText } from '../../utils/orderTruck';
import { massAccountLines } from '../../utils/massAccount';
import { orderHourlyText, orderQtyText } from '../../utils/orderHourly';
import InvoiceBadge from '../common/InvoiceBadge';
import { COLORS } from '../../styles/tokens';

const kr = n => `kr ${Number(n).toLocaleString('nb-NO')}`;

function whenLabel(date) {
  if (!date) return null;
  const day = date.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' });
  return `${day} kl. ${timeLabel(date)}`;
}

function priceInfo(order) {
  if (!hasFixedPrice(order)) return 'Åpent for bud';
  if (canTake(order)) return canBid(order) ? `Fastpris ${kr(order.fixedPrice)}, eller bud under` : `Fastpris ${kr(order.fixedPrice)}`;
  return `Maks ${kr(order.fixedPrice)}`;
}

// Resten av oppdragsinformasjonen som kompakte rader, vist inni det åpnede kortet («Se alle detaljer»).
// Ikke inntektsanslag og ikke avstand til henting.
export default function OrderInlineDetails({ order }) {
  const a = order.pickupAddress, b = order.deliveryAddress;
  const routeKm = a?.lat != null && b?.lat != null ? haversineKm(a.lat, a.lng, b.lat, b.lng) : null;
  const sub = getMassSubLabel(order);
  const mass = [massLabel(order.massType), sub].filter(Boolean).join(' · ');
  const qty = orderQtyText(order);
  const equipment = loadingEquipmentText(order);
  const deadline = isBidOrder(order) ? whenLabel(toDate(order.bidDeadline)) : null;

  const rows = [
    ['Massetype', mass],
    ['Mengde', qty],
    ['Timer', orderHourlyText(order)],
    ['Bil', orderTruckText(order)],
    ['Lass', orderLoadsText(order)],
    ['Fordeling', orderLoadSplitText(order)],
    ['Kranløft', orderCraneLiftText(order)?.replace('Kranløft: ', '')],
    ['Beskrivelse', order.massDescription?.trim() || null],
    ...massAccountLines(order).map(l => [l.label, l.value]),
    ['Ønsket levering', whenLabel(toDate(order.scheduledAt))],
    ['Budfrist', deadline],
    ['Lasteutstyr', equipment ? equipment.replace(/^Lasteutstyr: /, '') : null],
    ['Pris', priceInfo(order)],
    ['Rutelengde', routeKm != null ? kmLabel(routeKm) : null],
  ].filter(([, v]) => v);

  return (
    <dl data-testid="order-inline-details" style={styles.list}>
      {rows.map(([k, v]) => (
        <div key={k} style={styles.row}>
          <dt style={styles.k}>{k}</dt>
          <dd style={styles.v}>{v}</dd>
        </div>
      ))}
      <div style={styles.row}>
        <dt style={styles.k}>Betaling</dt>
        <dd style={styles.v}>
          {order.paymentMethod === 'invoice' ? <InvoiceBadge order={order} /> : 'Kort via Tippi'}
        </dd>
      </div>
    </dl>
  );
}

const styles = {
  list: { margin: '8px 0 0', padding: 0 },
  row:  { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '8px 0', borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 },
  k:    { color: COLORS.textMid, flexShrink: 0 },
  v:    { margin: 0, color: COLORS.textDark, fontWeight: 600, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' },
};
